import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { isMemberBlocked } from 'utils/member-utils';

const PRINT_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'printed', label: 'Printed' },
  { value: 'not_printed', label: 'Not Printed' },
];

const isPrintedStatus = (status) => {
  const normalized = (status || 'Not Printed').toString().trim().toLowerCase();
  return normalized === 'printed';
};

const MemberAllocationTable = ({
  members,
  sectorName,
  onPreview,
  onEditService,
  loading,
  onGotoSWAlloc,
  onRemoveService,
  onUnallocate,
  unallocatingServiceId,
  onRefresh,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [printFilter, setPrintFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const printCounts = useMemo(() => {
    const printed = members.filter((member) => isPrintedStatus(member.printStatus)).length;
    return {
      all: members.length,
      printed,
      not_printed: members.length - printed,
    };
  }, [members]);

  const filteredMembers = React.useMemo(() => {
    let next = members;

    if (printFilter === 'printed') {
      next = next.filter((member) => isPrintedStatus(member.printStatus));
    } else if (printFilter === 'not_printed') {
      next = next.filter((member) => !isPrintedStatus(member.printStatus));
    }

    if (!searchQuery) return next;

    const query = searchQuery.toLowerCase();
    return next.filter(memberObj =>
      (memberObj.name && memberObj.name.toLowerCase().includes(query)) ||
      (memberObj.mobNo && memberObj.mobNo.toString().includes(searchQuery)) ||
      (memberObj.servicePoint && memberObj.servicePoint.toLowerCase().includes(query)) ||
      (memberObj.district && memberObj.district.toLowerCase().includes(query)) ||
      (memberObj.samithi && memberObj.samithi.toLowerCase().includes(query)) ||
      (memberObj.age && memberObj.age.toString().toLowerCase().includes(query)) ||
      (memberObj.printStatus && memberObj.printStatus.toLowerCase().includes(query))
    );
  }, [members, searchQuery, printFilter]);

  const sortedMembers = React.useMemo(() => {
    if (!sortConfig.key) return filteredMembers;
    return [...filteredMembers].sort((a, b) => {
      
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredMembers, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon name="ArrowUp" size={16} className="text-primary" />
      : <Icon name="ArrowDown" size={16} className="text-primary" />;
  };

  const getAgeGroup = (age) => {
    if(age === "----") return { label: 'N/A', color: 'text-muted-foreground' };
    if (age < 25) return { label: 'Youth', color: 'text-success' };
    if (age < 45) return { label: 'Adult', color: 'text-primary' };
    if (age < 65) return { label: 'Senior', color: 'text-warning' };
    return { label: 'Elder', color: 'text-muted-foreground' };
  };

  // Initial sector load only — refresh keeps this view mounted
  if (loading && !isRefreshing) {
    return (
      <div className="bg-card rounded-lg border border-border mt-6 overflow-hidden">
        <div className="p-6">
          <div className="animate-spiritual-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border mt-6 overflow-hidden shadow-spiritual-sm">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h3 className="font-heading font-semibold text-lg text-foreground">
                {sectorName} - Member Allocations
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Detailed view of allocated members in this sector
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Users" size={16} />
                <span>{sortedMembers.length} members</span>
              </div>

              <div className="w-full sm:w-64">
                <Input
                  type="search"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>

              <Button
                variant="default"
                size="sm"
                onClick={() => onGotoSWAlloc()}
                iconPosition="left"
                className="hover:bg-accent"
              >
                Go To Sector Wise Allocation
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground mr-1">Card status:</span>
            <div
              className="inline-flex rounded-lg border border-border bg-muted/40 p-1"
              role="group"
              aria-label="Filter by print status"
            >
              {PRINT_FILTERS.map((filter) => {
                const isActive = printFilter === filter.value;
                return (
                  <button
                    key={filter.value}
                    type="button"
                    onClick={() => setPrintFilter(filter.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      isActive
                        ? filter.value === 'printed'
                          ? 'bg-success text-success-foreground shadow-sm'
                          : filter.value === 'not_printed'
                            ? 'bg-warning text-warning-foreground shadow-sm'
                            : 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-background/80'
                    }`}
                  >
                    {filter.label}
                    <span className={`ml-1.5 ${isActive ? 'opacity-90' : 'opacity-70'}`}>
                      ({printCounts[filter.value]})
                    </span>
                  </button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={handleRefresh}
              loading={isRefreshing}
              disabled={isRefreshing}
              aria-label="Refresh member list"
              className="sm:ml-2"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 font-heading font-medium text-foreground">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Name</span>
                  {getSortIcon('name')}
                </button>
              </th>
              <th className="text-left p-4 font-heading font-medium text-foreground">
                <button
                  onClick={() => handleSort('mobile')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Mobile</span>
                  {getSortIcon('mobNo')}
                </button>
              </th>
              <th className="text-left p-4 font-heading font-medium text-foreground">
                <button
                  onClick={() => handleSort('servicePoint')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Service Point</span>
                  {getSortIcon('servicePoint')}
                </button>
              </th>
              <th className="text-left p-4 font-heading font-medium text-foreground">
                <button
                  onClick={() => handleSort('district')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>District</span>
                  {getSortIcon('district')}
                </button>
              </th>
              <th className="text-left p-4 font-heading font-medium text-foreground">
                <button
                  onClick={() => handleSort('samithi')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors"
                >
                  <span>Samithi</span>
                  {getSortIcon('samithi')}
                </button>
              </th>
              <th className="text-center p-4 font-heading font-medium text-foreground">
                <button
                  onClick={() => handleSort('age')}
                  className="flex items-center space-x-2 hover:text-primary transition-colors mx-auto"
                >
                  <span>Age</span>
                  {getSortIcon('age')}
                </button>
              </th>
              <th className="text-center p-4 font-heading font-medium text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.map((memberDet, index) => {
              const ageGroup = getAgeGroup(memberDet.age);
              
              return (
                <tr
                  key={memberDet.id}
                  className={`
                    border-b border-border hover:bg-muted/30 transition-colors
                    ${index % 2 === 0 ? 'zebra-stripe' : ''}
                  `}
                >
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Icon name="User" size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-heading font-medium text-foreground">
                          {memberDet.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          ID: {memberDet.saiOneId || "----"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Phone" size={16} className="text-muted-foreground" />
                      <span className="font-mono text-foreground">{memberDet.mobNo || "------"}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="MapPin" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">{memberDet.servicePoint || "------"}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-foreground">{memberDet.district || "-------"}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-foreground">{memberDet.samithi || "------"}</span>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex flex-col items-center space-y-1">
                      <span className="text-lg font-heading font-semibold text-foreground">
                        {memberDet.age}
                      </span>
                      <span className={`text-xs font-caption ${ageGroup.color}`}>
                        {ageGroup.label}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onPreview(memberDet)}
                        iconName="Eye"
                        iconPosition="left"
                      >
                        Preview
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditService(memberDet)}
                        iconName="Edit"
                        iconPosition="left"
                      >
                        Edit Service
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div> */}

      <div className="overflow-x-auto">
    <div className="table w-full sector-table">
    
    {/* Table Header */}
    <div className="table-header-group bg-muted/50">
      <div className="table-row">
        <div className="table-cell text-left md:align-middle p-4 font-heading font-medium text-foreground">
          <button
            onClick={() => handleSort('name')}
            className="flex items-center space-x-2 hover:text-primary transition-colors"
          >
            <span>Name</span>
            {getSortIcon('name')}
          </button>
        </div>
        <div className="table-cell text-left md:align-middle p-4 font-heading font-medium text-foreground">
          <button
            onClick={() => handleSort('mobile')}
            className="flex items-center space-x-2 hover:text-primary transition-colors"
          >
            <span>Mobile</span>
            {getSortIcon('mobNo')}
          </button>
        </div>
        <div className="table-cell text-left md:align-middle p-4 font-heading font-medium text-foreground">
          <button
            onClick={() => handleSort('servicePoint')}
            className="flex items-center space-x-2 hover:text-primary transition-colors"
          >
            <span>Service Point</span>
            {getSortIcon('servicePoint')}
          </button>
        </div>
        <div className="table-cell text-left md:align-middle p-4 font-heading font-medium text-foreground">
          <button
            onClick={() => handleSort('district')}
            className="flex items-center space-x-2 hover:text-primary transition-colors"
          >
            <span>District</span>
            {getSortIcon('district')}
          </button>
        </div>
        <div className="table-cell text-left md:align-middle p-4 font-heading font-medium text-foreground">
          <button
            onClick={() => handleSort('samithi')}
            className="flex items-center space-x-2 hover:text-primary transition-colors"
          >
            <span>Samithi</span>
            {getSortIcon('samithi')}
          </button>
        </div>
        <div className="table-cell text-center md:align-middle p-4 font-heading font-medium text-foreground">
          <button
            onClick={() => handleSort('age')}
            className="flex items-center space-x-2 hover:text-primary transition-colors mx-auto"
          >
            <span>Age</span>
            {getSortIcon('age')}
          </button>
        </div>
        <div className="table-cell text-center md:align-middle p-4 font-heading font-medium text-foreground">
          Actions
        </div>
      </div>
    </div>

    {/* Table Body */}
    <div className="table-row-group">
      {sortedMembers.map((memberDet, index) => {
        const blocked = isMemberBlocked(memberDet);
        const ageGroup = getAgeGroup(memberDet.age);
        return (
          <div
            key={memberDet.id}
            className={`
              table-row border-b border-border transition-colors
              ${
                blocked
                  ? 'bg-red-50/80 dark:bg-red-950/20 hover:bg-red-100/80 border-l-4 border-l-red-500'
                  : index % 2 === 0 ? 'zebra-stripe hover:bg-muted/30' : 'hover:bg-muted/30'
              }
            `}
          >
            {/* Name */}
            <div className="table-cell p-4 mt-2 md:mt-0 md:align-middle" data-title="Name">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full hidden md:flex items-center justify-center ${
                  blocked ? 'bg-red-100 dark:bg-red-900/50' : 'bg-primary/10'
                }`}>
                  <Icon name="User" size={20} className={blocked ? 'text-red-600' : 'text-primary'} />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <p className={`font-heading font-medium ${blocked ? 'text-red-900 dark:text-red-300 font-semibold' : 'text-foreground'}`}>
                      {memberDet.name}
                    </p>
                    {blocked && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700">
                        Blocked
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    ID: {memberDet.saiOneId || "----"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                   Card : {memberDet.printStatus}
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile */}
            <div className="table-cell p-4 ml-3 md:mt-0 md:align-middle" data-title="Mobile">
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={16} className="text-muted-foreground" />
                <span className="font-mono text-foreground">{memberDet.mobNo || "------"}</span>
              </div>
            </div>

            {/* Service Point */}
            <div className="table-cell p-4 ml-3 md:mt-0 md:align-middle" data-title="Service Point">
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <span className="text-foreground">{memberDet.servicePoint || "------"}</span>
              </div>
            </div>

            {/* District */}
            <div className="table-cell p-4 ml-3 md:mt-0 md:align-middle" data-title="District">
              <span className="text-foreground">{memberDet.district || "-------"}</span>
            </div>

            {/* Samithi */}
            <div className="table-cell p-4 ml-3 md:mt-0 md:align-middle" data-title="Samithi">
              <span className="text-foreground">{memberDet.samithi || "------"}</span>
            </div>

            {/* Age */}
            <div className="table-cell p-4 mt-2 text-center md:align-middle" data-title="Age">
              <div className="flex flex-col items-center space-y-1">
                <span className="text-lg font-heading font-semibold text-foreground">
                  {memberDet.age}
                </span>
                <span className={`text-xs font-caption ${ageGroup.color}`}>
                  {ageGroup.label}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="table-cell p-4 text-center md:align-middle">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconSize="16"
                  onClick={() => onPreview(memberDet)}
                  iconName="Eye"
                  iconPosition="left"
                >
                  <span className='inline-block md:hidden lg:inline-block'>Preview</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconSize="16"
                  onClick={() => onEditService(memberDet)}
                  iconName="Edit"
                  iconPosition="left"
                >
                  <span className='inline-block md:hidden lg:inline-block'>Edit</span>                  
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconSize="16"
                  onClick={() => onUnallocate(memberDet)}
                  iconName="UserMinus"
                  iconPosition="left"
                  loading={unallocatingServiceId === memberDet.servicePointId}
                  disabled={Boolean(unallocatingServiceId)}
                >
                  <span className='inline-block md:hidden lg:inline-block'>Unallocate</span>                  
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconSize="16"
                  onClick={() => onRemoveService(memberDet)}
                  iconName="Delete"
                  iconPosition="left"
                >
                  <span className='inline-block md:hidden lg:inline-block'>Remove</span>                  
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>

  </div>
</div>


      {sortedMembers.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {searchQuery || printFilter !== 'all'
              ? 'No members found matching your filters'
              : 'No members allocated to this sector'}
          </p>
          {(searchQuery || printFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setPrintFilter('all');
              }}
              className="mt-2"
            >
              Clear filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default MemberAllocationTable;